/**
 * 3D Avatar Component with LAM Lip Sync + Life Layer
 *
 * Uses @omote/r3f's OmoteAvatar — replaces the 700-line AnimatedAvatar
 * with a ~30-line component that integrates with:
 * - Voice hook for LAM lip sync frames
 * - Stage store for avatar positioning
 * - FaceCompositor life layer (blinks, saccades, micro-motion)
 */

import { useCallback } from 'react'
import { createLogger } from '@omote/core/logging'
import { OmoteAvatar, preloadAvatar, type AvatarReadyEvent, type GazeConfig } from '@omote/r3f'
import { useVoiceFrame } from '../hooks/useVoice'
import { useAvatarPose } from '../scene'

const logger = createLogger('Avatar3D')

const DEFAULT_AVATAR_URL = '/models/avatar.glb'

const GAZE_CONFIG: GazeConfig = {
  enabled: true,
  target: 'camera',
  yawInfluence: 0.35,
  pitchInfluence: 0.25,
  smoothing: 4,
}

interface Avatar3DProps {
  scale?: number
  avatarUrl?: string
}

export default function Avatar3D({
  scale = 1,
  avatarUrl = DEFAULT_AVATAR_URL,
}: Avatar3DProps) {
  const { currentFrameRef, rawFrameRef, currentEmotion, stateRef, isSpeakingRef } = useVoiceFrame()
  const { position, rotation } = useAvatarPose()

  const handleReady = useCallback((event: AvatarReadyEvent) => {
    logger.info('Avatar loaded', {
      animations: event.animationControls.availableAnimations,
      morphTargets: Object.keys(event.morphTargetDictionary || {}).length,
    })
  }, [])

  return (
    <OmoteAvatar
      src={avatarUrl}
      frameRef={currentFrameRef}
      rawFrameRef={rawFrameRef}
      emotion={currentEmotion}
      stateRef={stateRef}
      isSpeakingRef={isSpeakingRef}
      gaze={GAZE_CONFIG}
      avatarScale={scale}
      sceneRotationY={0.5236}
      position={position as [number, number, number]}
      rotation={[0, rotation, 0]}
      onReady={handleReady}
    />
  )
}

// Preload default avatar
preloadAvatar(DEFAULT_AVATAR_URL)
